<template>
  <div class="timer-component">
    <!--
    <div class="connection-badge" :class="{ connected }">
      {{ connected ? 'Connected' : 'Disconnected' }}
    </div>
    -->

    <!--
    <div v-if="eventTitle" class="event-title">{{ eventTitle }}</div>
    -->

    <div
      v-if="!hideTimer"
      class="timer-display"
      :class="[phaseClass, { 'blink-overtime': shouldBlinkOvertime }]"
      :style="timerStyle"
    >
      {{ formattedTime }}
    </div>

    <!--
    <div class="playback-status">{{ statusLabel }}</div>
    -->
  </div>
</template>

<script>
import {
  OntimeService,
  ConnectionStatus,
  createConfig,
  TimerPlayback,
  TimerPhase,
  defaultTimerSettings
} from 'ontime-js'

export default {
  name: 'OntimeTimer',
  props: {
    url: {
      type: String,
      default: ''
    },
    host: {
      type: String,
      default: 'localhost'
    },
    port: {
      type: Number,
      default: 4001
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      connected: false,
      currentMs: null,
      playback: null,
      phase: null,
      eventTitle: null,
      service: null,
      eventSubscription: null,
      statusSubscription: null,
      onEndTimeout: null,
      showEndPlaceholder: false,
      hideTimer: false
    }
  },
  computed: {
    settings () {
      return {
        ...defaultTimerSettings,
        ...this.config
      }
    },
    formattedTime () {
      return this.formatMs(this.currentMs)
    },
    phaseClass () {
      switch (this.phase) {
        case TimerPhase.Warning:
          return 'warning'
        case TimerPhase.Danger:
          return 'danger'
        case TimerPhase.Overtime:
          return 'overtime'
        default:
          return 'default'
      }
    },
    statusLabel () {
      switch (this.playback) {
        case TimerPlayback.Play:
          return 'Playing'
        case TimerPlayback.Pause:
          return 'Paused'
        default:
          return 'Stopped'
      }
    },
    resolvedColor () {
      if (this.phase === TimerPhase.Warning) {
        return this.settings.warningColor
      }
      if (this.phase === TimerPhase.Danger || this.phase === TimerPhase.Overtime) {
        return this.settings.dangerColor
      }
      return this.settings.color
    },
    timerStyle () {
      if (!this.settings.overrideColors) {
        return null
      }
      return {
        color: this.resolvedColor
      }
    },
    shouldBlinkOvertime () {
      return this.phase === TimerPhase.Overtime && this.settings.blinkInOvertime
    }
  },
  mounted () {
    this.initService()
  },
  beforeDestroy () {
    this.disposeService()
  },
  methods: {
    getConfigFromUrl (rawUrl) {
      const parsed = new URL(rawUrl)
      const protocol = parsed.protocol.toLowerCase()

      if (!['http:', 'https:', 'ws:', 'wss:'].includes(protocol)) {
        throw new Error(`Unsupported protocol: ${protocol}`)
      }

      const isSecure = protocol === 'https:' || protocol === 'wss:'
      const port = parsed.port.length > 0 ? Number(parsed.port) : (isSecure ? 443 : 80)

      return createConfig(parsed.hostname, port, isSecure)
    },
    buildServiceConfig () {
      if (this.url && this.url.trim().length > 0) {
        return this.getConfigFromUrl(this.url.trim())
      }
      return createConfig(this.host, this.port)
    },
    clearOnEndTimeout () {
      if (this.onEndTimeout != null) {
        clearTimeout(this.onEndTimeout)
        this.onEndTimeout = null
      }
    },
    applyOnEndBehavior (timerCurrent) {
      this.clearOnEndTimeout()

      if (timerCurrent != null && timerCurrent <= 0) {
        if (this.settings.onEnd === 'hide') {
          this.hideTimer = true
          this.showEndPlaceholder = false
          return
        }

        if (this.settings.onEnd === 'hideAfterDelay') {
          this.hideTimer = false
          this.showEndPlaceholder = false
          this.onEndTimeout = setTimeout(() => {
            this.hideTimer = true
          }, this.settings.hideDelay)
          return
        }

        if (this.settings.onEnd === 'showPlaceholder') {
          this.hideTimer = false
          this.showEndPlaceholder = true
          return
        }
      }

      this.showEndPlaceholder = false
      this.hideTimer = false
    },
    initService () {
      this.disposeService()

      this.service = new OntimeService(this.buildServiceConfig())

      this.statusSubscription = this.service.status.subscribe((status) => {
        this.connected = status === ConnectionStatus.Connected
      })

      this.eventSubscription = this.service.events.subscribe((event) => {
        if (!event || event.tag !== 'timer') {
          return
        }

        const timerCurrent = event.timer.current
        this.currentMs = timerCurrent
        this.playback = event.timer.playback
        this.phase = event.timer.phase
        this.eventTitle = event.timer.title || null
        this.applyOnEndBehavior(timerCurrent)
      })

      this.service.init()
    },
    disposeService () {
      if (this.eventSubscription && this.eventSubscription.unsubscribe) {
        this.eventSubscription.unsubscribe()
      }
      if (this.statusSubscription && this.statusSubscription.unsubscribe) {
        this.statusSubscription.unsubscribe()
      }
      this.clearOnEndTimeout()
      if (this.service && this.service.close) {
        this.service.close()
      }

      this.eventSubscription = null
      this.statusSubscription = null
      this.service = null
    },
    formatMs (ms) {
      if (ms == null) {
        return this.settings.placeholder
      }

      if (this.showEndPlaceholder && this.settings.onEnd === 'showPlaceholder') {
        return this.settings.placeholder
      }

      const sign = ms < 0 && this.settings.showMinus ? '-' : ''
      const abs = Math.abs(ms)
      const totalSeconds = ms >= 0
        ? Math.ceil(ms / 1000)
        : Math.floor(abs / 1000)
      const totalMinutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours > 0) {
        return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      }

      const minutesPart = this.settings.showLeadingZeroOnMinutes
        ? String(totalMinutes).padStart(2, '0')
        : String(totalMinutes)

      return `${sign}${minutesPart}:${String(seconds).padStart(2, '0')}`
    }
  },
  watch: {
    url () {
      this.initService()
    },
    host () {
      if (!this.url || this.url.trim().length === 0) {
        this.initService()
      }
    },
    port () {
      if (!this.url || this.url.trim().length === 0) {
        this.initService()
      }
    }
  }
}
</script>

<style scoped>
.timer-component {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  font-family: monospace;
  padding: 1rem;
}

.connection-badge {
  font-size: 0.75rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: #555;
  color: #fff;
}

.connection-badge.connected {
  background: #2ecc71;
}

.timer-display {
  font-size: 4rem;
  font-weight: bold;
  letter-spacing: 0.05em;
}

.timer-display.default {
  color: #666;
}

.timer-display.warning {
  color: #ffab33;
}

.timer-display.danger {
  color: #ed3333;
}

.timer-display.overtime {
  color: #ed3333;
}

.timer-display.overtime.blink-overtime {
  animation: blink 1s step-start infinite;
}

@keyframes blink {
  50% { opacity: 0; }
}

.playback-status {
  font-size: 0.9rem;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.event-title {
  font-size: 1.2rem;
  color: #ddd;
}
</style>



// WEBPACK FOOTER //
// src/components/ontime/OntimeTimer.vue